import React, { useEffect, useRef, useState } from "react";
import { api, API_BASE } from "@/lib/api";
import { toast } from "sonner";
import {
  Bold, Italic, Underline, List, Image as ImageIcon,
  Type, Palette, Link as LinkIcon, Eraser, Loader2, Film,
} from "lucide-react";

const FONT_SIZES = [
  { label: "S", value: "2" },
  { label: "M", value: "3" },
  { label: "L", value: "5" },
  { label: "XL", value: "6" },
  { label: "XXL", value: "7" },
];

const COLORS = [
  "#F8FAFC", "#94A3B8", "#3FBF3F", "#1E90FF", "#C0C0C0",
  "#FFD166", "#EF476F", "#06D6A0",
];

const FONT_FAMILIES = [
  { label: "Default", value: "" },
  { label: "Outfit", value: "Outfit, sans-serif" },
  { label: "Manrope", value: "Manrope, sans-serif" },
  { label: "Georgia", value: "Georgia, serif" },
  { label: "Courier", value: "Courier New, monospace" },
];

const exec = (cmd, value = null) => document.execCommand(cmd, false, value);

// Convert various video URLs to an embeddable iframe/video HTML snippet.
const toEmbedHtml = (rawUrl) => {
  const url = (rawUrl || "").trim();
  if (!url) return "";
  // YouTube
  const yt = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|v\/|shorts\/)|youtu\.be\/)([A-Za-z0-9_-]{6,})/);
  if (yt) {
    return `<div class="rt-embed"><iframe src="https://www.youtube.com/embed/${yt[1]}" title="YouTube video" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen></iframe></div>`;
  }
  // Vimeo
  const vm = url.match(/vimeo\.com\/(?:video\/)?(\d{6,})/);
  if (vm) {
    return `<div class="rt-embed"><iframe src="https://player.vimeo.com/video/${vm[1]}" title="Vimeo video" frameborder="0" allow="autoplay; fullscreen; picture-in-picture" allowfullscreen></iframe></div>`;
  }
  // Direct video file
  if (/\.(mp4|webm|mov|ogg|m4v)(\?|#|$)/i.test(url)) {
    return `<video controls preload="metadata" src="${url}" style="width:100%;max-width:100%;border-radius:0.75rem;margin:1em 0;"></video>`;
  }
  // Fallback to iframe (admin-only feature so trust the URL)
  return `<div class="rt-embed"><iframe src="${url}" frameborder="0" allowfullscreen></iframe></div>`;
};

const resolveAssetUrl = (url) => {
  if (!url) return "";
  if (url.startsWith("http")) return url;
  if (url.startsWith("/api/")) return API_BASE.replace(/\/api$/, "") + url;
  return url;
};

export const RichTextEditor = ({ value = "", onChange, placeholder = "Type here...", minHeight = 90, testId }) => {
  const ref = useRef(null);
  const imgRef = useRef(null);
  const vidRef = useRef(null);
  const [empty, setEmpty] = useState(!value);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (ref.current && ref.current.innerHTML !== value) {
      ref.current.innerHTML = value || "";
      setEmpty(!value);
    }
  }, [value]);

  const handleInput = () => {
    if (!ref.current) return;
    const html = ref.current.innerHTML;
    setEmpty(!ref.current.textContent && !ref.current.querySelector("img,video,iframe"));
    onChange?.(html);
  };

  const apply = (cmd, val = null) => {
    ref.current?.focus();
    exec(cmd, val);
    handleInput();
  };

  const insertHTML = (html) => {
    ref.current?.focus();
    exec("insertHTML", html);
    handleInput();
  };

  const handleUpload = async (file, asVideo = false) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/uploads", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const url = resolveAssetUrl(res.data.url);
      if (asVideo || res.data.kind === "video") {
        insertHTML(`<video controls preload="metadata" src="${url}" style="width:100%;max-width:100%;border-radius:0.75rem;margin:1em 0;"></video><p></p>`);
      } else {
        insertHTML(`<img src="${url}" alt="" style="max-width:100%;border-radius:0.75rem;margin:1em 0;" /><p></p>`);
      }
      toast.success(`${asVideo ? "Video" : "Image"} inserted`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  const handleImageButton = () => imgRef.current?.click();

  const handleVideoButton = () => {
    const mode = window.prompt(
      "Insert video — choose:\n  1 = Upload a file\n  2 = Embed a YouTube / Vimeo / video URL",
      "2"
    );
    if (!mode) return;
    if (mode.trim() === "1") {
      vidRef.current?.click();
    } else {
      const url = window.prompt("Paste a YouTube, Vimeo, or direct video URL:");
      if (!url) return;
      const html = toEmbedHtml(url);
      if (!html) { toast.error("Couldn't parse that URL"); return; }
      insertHTML(html + "<p></p>");
    }
  };

  const insertLink = () => {
    const url = window.prompt("Link URL:");
    if (url) apply("createLink", url);
  };

  return (
    <div className="rte-wrap" data-testid={testId}>
      <input ref={imgRef} type="file" accept="image/*" className="hidden"
        onChange={(e) => { handleUpload(e.target.files?.[0], false); e.target.value=""; }} />
      <input ref={vidRef} type="file" accept="video/*" className="hidden"
        onChange={(e) => { handleUpload(e.target.files?.[0], true); e.target.value=""; }} />

      <div className="rte-toolbar flex flex-wrap items-center gap-1 mb-2">
        <button type="button" onClick={() => apply("bold")} title="Bold" data-testid="rte-bold"><Bold className="h-3.5 w-3.5" /></button>
        <button type="button" onClick={() => apply("italic")} title="Italic" data-testid="rte-italic"><Italic className="h-3.5 w-3.5" /></button>
        <button type="button" onClick={() => apply("underline")} title="Underline" data-testid="rte-underline"><Underline className="h-3.5 w-3.5" /></button>
        <button type="button" onClick={() => apply("insertUnorderedList")} title="List" data-testid="rte-list"><List className="h-3.5 w-3.5" /></button>
        <span className="mx-1 h-5 w-px bg-white/10" />
        <div className="inline-flex items-center gap-1">
          <Type className="h-3.5 w-3.5 text-slate-400" />
          {FONT_SIZES.map((s) => (
            <button key={s.value} type="button" onClick={() => apply("fontSize", s.value)} className="!px-2">{s.label}</button>
          ))}
        </div>
        <span className="mx-1 h-5 w-px bg-white/10" />
        <select
          onChange={(e) => apply("fontName", e.target.value)}
          className="bg-slate-950 border border-white/10 text-xs rounded px-2 py-1 text-slate-200"
          title="Font family"
          defaultValue=""
        >
          {FONT_FAMILIES.map((f) => (
            <option key={f.label} value={f.value}>{f.label}</option>
          ))}
        </select>
        <span className="mx-1 h-5 w-px bg-white/10" />
        <div className="inline-flex items-center gap-1">
          <Palette className="h-3.5 w-3.5 text-slate-400" />
          {COLORS.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => apply("foreColor", c)}
              style={{ background: c }}
              className="!h-5 !w-5 !p-0 rounded-full border border-white/20"
              aria-label={`color ${c}`}
            />
          ))}
        </div>
        <span className="mx-1 h-5 w-px bg-white/10" />
        <button type="button" onClick={insertLink} title="Link" data-testid="rte-link"><LinkIcon className="h-3.5 w-3.5" /></button>
        <button type="button" onClick={handleImageButton} title="Upload image" data-testid="rte-image">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ImageIcon className="h-3.5 w-3.5" />}
        </button>
        <button type="button" onClick={handleVideoButton} title="Insert video (upload or YouTube/Vimeo)" data-testid="rte-video">
          <Film className="h-3.5 w-3.5" />
        </button>
        <button type="button" onClick={() => apply("removeFormat")} title="Clear formatting" data-testid="rte-clear"><Eraser className="h-3.5 w-3.5" /></button>
      </div>
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onInput={handleInput}
        onBlur={handleInput}
        data-placeholder={placeholder}
        data-empty={empty || undefined}
        className="rte-area"
        style={{ minHeight }}
      />
    </div>
  );
};

export default RichTextEditor;
